import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { BorderBeam } from "../lib/border-beam";

/**
 * Badge — small status pill. Covers the semantic states + the product
 * taxonomy pills that consumer apps actually need (public/private/premium).
 *
 * Every semantic variant routes through CSS-variable tokens (`--success`,
 * `--warning`, `--info`, `--premium`) so per-studio retinting + dark mode
 * just work. If you need a literal color (e.g., a brand-partner pill),
 * reach for `outline` and pass a className override — don't add a one-off
 * variant here.
 *
 * Appearance axis (additive on top of variant):
 *   - `"solid"` (default) — current behavior. The variant drives bg + border + text.
 *   - `"soft"` — pale tinted chip. Drops bg to /15 and border to /30 alpha,
 *     matching the existing `secondary` variant treatment. Use in dense rows.
 *   - `"steel"` — navy gradient container via `--steel-*` tokens. Renders
 *     the BrandTile / AikoMark steel chrome on a badge shape. Applied via
 *     inline style (gradient + shadow can't be expressed as Tailwind utilities
 *     without arbitrary CSS var references). Use sparingly — for premium /
 *     brand-tier surfaces only.
 *
 * Use sparingly — a wall of badges loses meaning. One per surface, max two.
 */
export const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
  {
    variants: {
      variant: {
        // Core neutrals
        default: "border-transparent bg-primary text-primary-foreground",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground",
        outline: "text-foreground border-input",
        // Semantic states (token-driven)
        success: "border-success/30 bg-success/15 text-success",
        warning: "border-warning/30 bg-warning/15 text-warning",
        info: "border-info/30 bg-info/15 text-info",
        destructive:
          "border-destructive/30 bg-destructive/15 text-destructive",
        accent: "border-accent/30 bg-accent/15 text-accent",
        // Product taxonomy
        live: "border-success/30 bg-success/15 text-success",
        new: "border-transparent bg-accent text-accent-foreground",
        beta: "border-info/30 bg-info/15 text-info",
        public: "border-success/25 bg-success/10 text-success",
        private: "border-border bg-muted/50 text-muted-foreground",
        premium: "border-premium/30 bg-premium/15 text-premium",
        // Editorial — content states the platform applies on top of the
        // semantic state. `published` reads the same as a positive success
        // signal (the work shipped), `recommendation` reads as info with
        // softer fill (an editorial nudge, not a system status).
        published: "border-success/30 bg-success/15 text-success",
        recommendation: "border-info/20 bg-info/10 text-info",
      },
      appearance: {
        // solid — default. The variant drives all styling (preserves all 16
        // existing variants identical to prior behavior).
        solid: "",
        // soft — pale tinted chip. Overrides the variant's bg/border to a
        // lighter alpha: /15 fill + /30 border. Text color stays token-driven.
        // Matches the existing `secondary` soft aesthetic.
        soft: "bg-opacity-[0.15] [background-color:hsl(var(--info)/0.15)] [border-color:hsl(var(--info)/0.30)]",
        // steel — navy gradient. Applied via inline style in the component
        // because Tailwind cannot resolve arbitrary CSS var gradients/shadows
        // as utility classes. This variant class just signals the intent;
        // the component applies inline style when appearance="steel".
        steel: "border-transparent text-white",
      },
      size: {
        sm: "px-2 py-0 text-[10px]",
        default: "px-2.5 py-0.5 text-xs",
        lg: "px-3 py-1 text-sm",
      },
    },
    defaultVariants: { variant: "default", size: "default", appearance: "solid" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Wrap the badge in an animated `<BorderBeam>`. Use sparingly — only
   * for "earned attention" surfaces (premium tier, "new" feature, etc.).
   * Reduce-motion users get a static border, no rotation.
   */
  shimmer?: boolean;
  /** BorderBeam color override (defaults to `hsl(var(--accent))`). */
  shimmerColor?: string;
  /** BorderBeam loop duration in seconds (default 4). */
  shimmerDuration?: number;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant,
      size,
      appearance = "solid",
      shimmer = false,
      shimmerColor,
      shimmerDuration,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    // Steel appearance: navy gradient + inset highlight + outer shadow.
    // Applied as inline style because Tailwind cannot compose arbitrary CSS
    // var references into gradient/shadow utilities. Matches AikoMark's
    // steelStyle pattern exactly so the visual identity is consistent.
    const steelStyle: React.CSSProperties =
      appearance === "steel"
        ? {
            background:
              "linear-gradient(160deg, var(--steel-bg, #1B4F7E) 0%, var(--steel-bg-to, #0A2540) 100%)",
            borderColor: "var(--steel-border, rgba(255,255,255,0.18))",
            boxShadow: [
              "var(--steel-shadow-inset, inset 0 1px 0 rgba(255,255,255,0.18))",
              "var(--steel-shadow-outer, 0 6px 14px -6px rgba(10,37,64,0.55))",
            ].join(", "),
            color: "var(--steel-fg, #ffffff)",
          }
        : {};

    const inner = (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size, appearance }), className)}
        style={{ ...steelStyle, ...style }}
        {...props}
      >
        {children}
      </span>
    );
    if (!shimmer) return inner;
    return (
      <BorderBeam
        color={shimmerColor}
        duration={shimmerDuration}
        radius="9999px"
      >
        {inner}
      </BorderBeam>
    );
  },
);
Badge.displayName = "Badge";
